import { MaterialCommunityIcons } from "@expo/vector-icons";
import Constants from "expo-constants";
import { useEffect, useState } from "react";
import {
    Alert,
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";

import {
    type ApiEnvironment,
    type PresetKey,
    PRESETS,
    activePresetKey,
    getActiveEnv,
    setApiEnv
} from "@/lib/api-env";
import { colors, fonts, radius, shadows, spacing, typography } from "@/lib/theme";

interface Props {
    visible: boolean;
    onClose: () => void;
}

const PRESET_ENTRIES: { key: PresetKey; icon: string; color: string }[] = [
    { key: "production", icon: "rocket-launch", color: colors.error500 },
    { key: "staging", icon: "flask", color: colors.warning500 },
    { key: "local", icon: "laptop", color: colors.teal500 }
];

export function DevEnvironmentSheet({ visible, onClose }: Props) {
    const [selected, setSelected] = useState<PresetKey | "custom">(activePresetKey());
    const [customName, setCustomName] = useState("");
    const [customApiUrl, setCustomApiUrl] = useState("");
    const [customWsUrl, setCustomWsUrl] = useState("");
    const [saving, setSaving] = useState(false);

    // Sync state when sheet opens
    useEffect(() => {
        if (visible) {
            const current = activePresetKey();
            setSelected(current);
            if (current === "custom") {
                const env = getActiveEnv();
                setCustomName(env.name);
                setCustomApiUrl(env.apiUrl);
                setCustomWsUrl(env.wsUrl);
            }
        }
    }, [visible]);

    if (!visible) {
        return null;
    }

    const appVersion = Constants.expoConfig?.version ?? "—";
    const activeEnv = getActiveEnv();

    async function handleSave() {
        setSaving(true);
        try {
            let env: ApiEnvironment;
            if (selected === "custom") {
                if (!customApiUrl.trim()) {
                    Alert.alert("Missing URL", "Please enter an API URL.");
                    setSaving(false);
                    return;
                }
                env = {
                    name: customName.trim() || "Custom",
                    apiUrl: customApiUrl.trim().replace(/\/+$/, ""),
                    wsUrl:
                        customWsUrl.trim().replace(/\/+$/, "") ||
                        customApiUrl.trim().replace(/\/+$/, "").replace(/^http/, "ws")
                };
            } else {
                env = PRESETS[selected];
            }
            await setApiEnv(env);
            onClose();
            Alert.alert(
                "Environment switched",
                `Now using ${env.name}.\nRestart the app for changes to take full effect.`
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <View style={styles.overlay}>
            <Pressable style={styles.backdrop} onPress={onClose} />
            <View style={styles.sheet}>
                <ScrollView bounces={false} showsVerticalScrollIndicator={false}>
                    {/* Header */}
                    <View style={styles.header}>
                        <MaterialCommunityIcons
                            name="server-network"
                            size={20}
                            color={colors.teal500}
                        />
                        <Text style={styles.title}>API Environment</Text>
                    </View>

                    {/* Current */}
                    <View style={styles.currentBox}>
                        <Text style={styles.currentLabel}>Active</Text>
                        <Text style={styles.currentName}>{activeEnv.name}</Text>
                        <Text style={styles.currentUrl} numberOfLines={1}>
                            {activeEnv.apiUrl}
                        </Text>
                    </View>

                    {/* Presets */}
                    <Text style={styles.sectionLabel}>Presets</Text>
                    {PRESET_ENTRIES.map(({ key, icon, color }) => {
                        const preset = PRESETS[key];
                        const isActive = selected === key;
                        return (
                            <TouchableOpacity
                                key={key}
                                activeOpacity={0.7}
                                style={[styles.presetRow, isActive && styles.presetRowActive]}
                                onPress={() => setSelected(key)}
                            >
                                <View
                                    style={[styles.presetIcon, { backgroundColor: color + "18" }]}
                                >
                                    <MaterialCommunityIcons
                                        name={icon as any}
                                        size={18}
                                        color={color}
                                    />
                                </View>
                                <View style={{ flex: 1 }}>
                                    <Text style={styles.presetName}>{preset.name}</Text>
                                    <Text style={styles.presetUrl} numberOfLines={1}>
                                        {preset.apiUrl}
                                    </Text>
                                </View>
                                {isActive && (
                                    <MaterialCommunityIcons
                                        name="check-circle"
                                        size={20}
                                        color={colors.teal500}
                                    />
                                )}
                            </TouchableOpacity>
                        );
                    })}

                    {/* Local platform note */}
                    {Platform.OS === "android" && (
                        <Text style={styles.platformNote}>
                            Android uses 10.0.2.2 to reach host machine
                        </Text>
                    )}

                    {/* Custom */}
                    <Text style={[styles.sectionLabel, { marginTop: spacing.lg }]}>Custom</Text>
                    <TouchableOpacity
                        activeOpacity={0.7}
                        style={[styles.presetRow, selected === "custom" && styles.presetRowActive]}
                        onPress={() => setSelected("custom")}
                    >
                        <View
                            style={[styles.presetIcon, { backgroundColor: colors.pink400 + "18" }]}
                        >
                            <MaterialCommunityIcons
                                name="pencil-outline"
                                size={18}
                                color={colors.pink400}
                            />
                        </View>
                        <Text style={styles.presetName}>Custom URL</Text>
                        {selected === "custom" && (
                            <MaterialCommunityIcons
                                name="check-circle"
                                size={20}
                                color={colors.teal500}
                            />
                        )}
                    </TouchableOpacity>

                    {selected === "custom" && (
                        <View style={styles.customInputs}>
                            <Text style={styles.inputLabel}>Name</Text>
                            <TextInput
                                style={styles.input}
                                value={customName}
                                onChangeText={setCustomName}
                                placeholder="My server"
                                placeholderTextColor={colors.textTertiary}
                            />
                            <Text style={styles.inputLabel}>API URL</Text>
                            <TextInput
                                style={styles.input}
                                value={customApiUrl}
                                onChangeText={setCustomApiUrl}
                                placeholder="https://my-api.example.com"
                                placeholderTextColor={colors.textTertiary}
                                autoCapitalize="none"
                                autoCorrect={false}
                                keyboardType="url"
                            />
                            <Text style={styles.inputLabel}>WebSocket URL (optional)</Text>
                            <TextInput
                                style={styles.input}
                                value={customWsUrl}
                                onChangeText={setCustomWsUrl}
                                placeholder="Auto-derived from API URL"
                                placeholderTextColor={colors.textTertiary}
                                autoCapitalize="none"
                                autoCorrect={false}
                                keyboardType="url"
                            />
                        </View>
                    )}

                    {/* Save */}
                    <TouchableOpacity
                        activeOpacity={0.7}
                        style={[styles.saveBtn, saving && { opacity: 0.6 }]}
                        onPress={handleSave}
                        disabled={saving}
                    >
                        <Text style={styles.saveBtnText}>
                            {saving ? "Saving..." : "Save & Restart"}
                        </Text>
                    </TouchableOpacity>

                    {/* Version */}
                    <Text style={styles.version}>v{appVersion}</Text>
                </ScrollView>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    overlay: {
        ...StyleSheet.absoluteFill,
        zIndex: 200,
        justifyContent: "flex-end"
    },
    backdrop: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "rgba(15, 10, 40, 0.5)"
    },
    sheet: {
        backgroundColor: colors.surface,
        borderTopLeftRadius: radius.xl,
        borderTopRightRadius: radius.xl,
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.lg,
        paddingBottom: spacing["3xl"],
        maxHeight: "85%",
        ...shadows.xl
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        marginBottom: spacing.base
    },
    title: {
        ...typography.h2,
        color: colors.textPrimary
    },
    currentBox: {
        backgroundColor: colors.teal50,
        borderRadius: radius.md,
        padding: spacing.base,
        marginBottom: spacing.lg,
        borderWidth: 1,
        borderColor: colors.teal200
    },
    currentLabel: {
        ...typography.label,
        color: colors.teal600,
        textTransform: "uppercase",
        marginBottom: spacing.xs
    },
    currentName: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    currentUrl: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: 2
    },
    sectionLabel: {
        ...typography.label,
        color: colors.textTertiary,
        textTransform: "uppercase",
        marginBottom: spacing.sm
    },
    presetRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.base,
        borderRadius: radius.md,
        marginBottom: spacing.xs,
        borderWidth: 1.5,
        borderColor: "transparent"
    },
    presetRowActive: {
        backgroundColor: colors.gray50,
        borderColor: colors.teal400
    },
    presetIcon: {
        width: 34,
        height: 34,
        borderRadius: 17,
        alignItems: "center",
        justifyContent: "center"
    },
    presetName: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    presetUrl: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: 1
    },
    platformNote: {
        ...typography.caption,
        color: colors.textTertiary,
        fontStyle: "italic",
        marginTop: spacing.xs,
        marginLeft: spacing.base
    },
    customInputs: {
        paddingHorizontal: spacing.sm,
        marginTop: spacing.sm
    },
    inputLabel: {
        ...typography.caption,
        color: colors.textSecondary,
        marginBottom: spacing.xs,
        marginTop: spacing.md
    },
    input: {
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: radius.sm,
        paddingHorizontal: spacing.base,
        paddingVertical: Platform.OS === "ios" ? spacing.md : spacing.sm,
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.textPrimary,
        backgroundColor: colors.gray50
    },
    saveBtn: {
        backgroundColor: colors.teal500,
        borderRadius: radius.md,
        paddingVertical: spacing.base,
        alignItems: "center",
        marginTop: spacing.xl
    },
    saveBtnText: {
        ...typography.subtitle,
        color: "#FFFFFF"
    },
    version: {
        ...typography.caption,
        color: colors.textTertiary,
        textAlign: "center",
        marginTop: spacing.base
    }
});
